/**
 * Debug override registry — frozen query hashes for the stage-simulator devtool.
 *
 * A query whose hash is registered here has had debug data injected via
 * `injectDebugQueryData` (`src/devtool/inject-query-data.ts`). While registered,
 * `useRealtimeQuery` skips applying live NATS patches to that query so the
 * injected fixture is not reverted by a real chain event.
 *
 * Pinned on `globalThis` (NOT a module-level Set) because the writer
 * (`injectDebugQueryData`, in the code-split `/devtool` bundle) and the reader
 * (`useRealtimeQuery`, in the `/client` bundle) resolve through different
 * entry-point graphs; a module-level singleton would be duplicated, so the
 * freeze guard would never see the writer's entries. Matches the SDK's other
 * cross-bundle singletons. Empty in production builds because the only writer
 * is reachable solely through the engineer-gated `/devtool` surface.
 */

import { hashKey, type QueryKey } from '@tanstack/react-query';

declare global {
  // eslint-disable-next-line no-var
  var __morfi_debug_overrides__: Set<string> | undefined;
}

function overrides(): Set<string> {
  if (globalThis.__morfi_debug_overrides__ === undefined) {
    globalThis.__morfi_debug_overrides__ = new Set<string>();
  }
  return globalThis.__morfi_debug_overrides__;
}

/** Register a query hash as debug-overridden (freezes realtime for it). */
export function markDebugOverride(queryHash: string): void {
  overrides().add(queryHash);
}

/** Remove a query hash from the override set (unfreezes realtime). */
export function clearDebugOverride(queryHash: string): void {
  overrides().delete(queryHash);
}

/** True when the query hash currently has injected debug data pinned. */
export function isDebugOverridden(queryHash: string): boolean {
  return overrides().has(queryHash);
}

/** True when a TanStack query key currently has injected debug data pinned. */
export function isQueryDebugOverridden(queryKey: QueryKey): boolean {
  return isDebugOverridden(hashKey(queryKey));
}

/** Test-only — clears every override. */
export function __resetDebugOverridesForTests(): void {
  globalThis.__morfi_debug_overrides__?.clear();
}
